import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Ban, Flag, Loader2, Send, MessageSquare, ArrowLeft } from "lucide-react";

export default function Messages() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [convos, setConvos] = useState(null);
  const [thread, setThread] = useState(null);
  const [body, setBody] = useState("");
  const endRef = useRef(null);

  const loadConvos = () => api.get("/conversations").then(({ data }) => setConvos(data)).catch(() => setConvos([]));
  useEffect(() => { loadConvos(); }, []);

  const loadThread = () => {
    if (!id) { setThread(null); return; }
    api.get(`/conversations/${id}/messages`).then(({ data }) => setThread(data)).catch(() => toast.error("Could not load conversation"));
  };
  useEffect(loadThread, [id]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    try {
      await api.post(`/conversations/${id}/messages`, { body });
      setBody(""); loadThread(); loadConvos();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const block = async () => {
    try { await api.post(`/conversations/${id}/block`); toast.success("Conversation blocked"); loadThread(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const report = async () => {
    try { await api.post("/reports", { target_type: "user", target_id: id, reason: "Reported from conversation" }); toast.success("Reported to Vorra moderation"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const other = (c) => (user?.user_id === c.buyer_id ? c.seller_name : c.buyer_name) || "User";

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="font-heading text-2xl font-bold mb-6">Messages</h1>
      <div className="grid md:grid-cols-3 gap-6 bg-card border border-border rounded-2xl overflow-hidden min-h-[520px]">
        {/* list */}
        <div className={`border-r border-border ${id ? "hidden md:block" : "block"}`}>
          {convos === null ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-accent" /></div>
            : convos.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground"><MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-40" />No conversations yet.</div>
            : convos.map((c) => (
              <button key={c.id} onClick={() => navigate(`/messages/${c.id}`)} data-testid={`conversation-${c.id}`}
                className={`w-full text-left p-4 border-b border-border hover:bg-muted/50 ${id === c.id ? "bg-muted" : ""}`}>
                <div className="flex justify-between items-center">
                  <span className="font-medium text-sm truncate">{other(c)}</span>
                  {c.closed && <span className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">Closed</span>}
                </div>
                <p className="text-xs text-muted-foreground truncate">{c.listing_title}</p>
                {c.last_message && <p className="text-xs text-muted-foreground truncate mt-1">{c.last_message}</p>}
              </button>
            ))}
        </div>

        {/* thread */}
        <div className="md:col-span-2 flex flex-col">
          {!id ? <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">Select a conversation</div>
            : !thread ? <div className="flex-1 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-accent" /></div>
            : (
              <>
                <div className="p-4 border-b border-border flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <Button variant="ghost" size="icon" className="md:hidden" onClick={() => navigate("/messages")}><ArrowLeft className="w-4 h-4" /></Button>
                    <div className="min-w-0"><p className="font-semibold truncate">{other(thread.conversation)}</p><p className="text-xs text-muted-foreground truncate">{thread.conversation.listing_title}</p></div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={report} data-testid="report-btn"><Flag className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={block} data-testid="block-btn"><Ban className="w-4 h-4" /></Button>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[420px]">
                  {thread.messages.map((m) => (
                    <div key={m.id} className={`flex ${m.sender_id === user?.user_id ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm ${m.sender_id === user?.user_id ? "bg-accent text-white rounded-br-sm" : "bg-muted rounded-bl-sm"}`}>{m.body}</div>
                    </div>
                  ))}
                  <div ref={endRef} />
                </div>
                {thread.conversation.closed || thread.conversation.blocked_by?.length ? (
                  <div className="p-4 border-t border-border text-center text-sm text-muted-foreground">
                    {thread.conversation.closed ? "This conversation is closed (vehicle sold or removed)." : "Messaging is blocked."}
                  </div>
                ) : (
                  <form onSubmit={send} className="p-4 border-t border-border flex gap-2">
                    <Input data-testid="message-input" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Type a message…" />
                    <Button type="submit" className="bg-accent hover:bg-emerald-600 text-white" data-testid="send-message-btn"><Send className="w-4 h-4" /></Button>
                  </form>
                )}
              </>
            )}
        </div>
      </div>
    </div>
  );
}
